/**
 * Reading Meta Graph insight rows. Graph returns every figure as a string
 * and purchases as an `actions` list; these turn that into numbers the
 * strip can show, and never throw on a shape we did not expect.
 */

export interface InsightTotals {
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
}

export interface InsightSeries {
  spend: Record<string, number>;
  impressions: Record<string, number>;
  clicks: Record<string, number>;
  purchases: Record<string, number>;
}

const PURCHASE_ACTIONS = new Set(["purchase", "omni_purchase", "offsite_conversion.fb_pixel_purchase"]);

type Row = Record<string, unknown>;

function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function rowsOf(res: unknown): Row[] {
  if (res && typeof res === "object" && Array.isArray((res as { data?: unknown }).data)) {
    return (res as { data: Row[] }).data;
  }
  return [];
}

/** Purchases from an insight row's `actions`, the first matching type wins. */
export function purchasesOf(row: Row): number {
  const actions = row.actions;
  if (!Array.isArray(actions)) return 0;
  for (const a of actions as Row[]) {
    if (typeof a.action_type === "string" && PURCHASE_ACTIONS.has(a.action_type)) return num(a.value);
  }
  return 0;
}

export function totalsOf(rows: Row[]): InsightTotals {
  return rows.reduce<InsightTotals>(
    (acc, row) => ({
      spend: acc.spend + num(row.spend),
      impressions: acc.impressions + num(row.impressions),
      clicks: acc.clicks + num(row.clicks),
      purchases: acc.purchases + purchasesOf(row),
    }),
    { spend: 0, impressions: 0, clicks: 0, purchases: 0 }
  );
}

/** One value per day, keyed by the row's `date_start`. */
export function seriesOf(rows: Row[]): InsightSeries {
  const series: InsightSeries = { spend: {}, impressions: {}, clicks: {}, purchases: {} };
  for (const row of rows) {
    const key = typeof row.date_start === "string" ? row.date_start : null;
    if (!key) continue;
    series.spend[key] = (series.spend[key] ?? 0) + num(row.spend);
    series.impressions[key] = (series.impressions[key] ?? 0) + num(row.impressions);
    series.clicks[key] = (series.clicks[key] ?? 0) + num(row.clicks);
    series.purchases[key] = (series.purchases[key] ?? 0) + purchasesOf(row);
  }
  return series;
}

/** `{ since, until }` for the last `days` days and the `days` before them. */
export function windows(days: number) {
  const day = 86_400_000;
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const until = new Date();
  const since = new Date(until.getTime() - (days - 1) * day);
  const prevUntil = new Date(since.getTime() - day);
  const prevSince = new Date(prevUntil.getTime() - (days - 1) * day);
  return {
    current: { since: iso(since), until: iso(until) },
    previous: { since: iso(prevSince), until: iso(prevUntil) },
  };
}

/** "OUTCOME_TRAFFIC" → "Traffic", "PAUSED" → "Paused". */
export function humanize(value: unknown): string {
  if (typeof value !== "string" || !value) return "—";
  const words = value.replace(/^OUTCOME_/, "").toLowerCase().split("_");
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");
}

/** Meta budgets arrive in minor units as strings. */
export function budgetOf(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n / 100 : null;
}

/** Short form of a long numeric Meta id: "…957314". */
export function shortId(value: unknown): string {
  const s = String(value ?? "");
  return /^\d{11,}$/.test(s) ? `…${s.slice(-6)}` : s || "—";
}
